// PersonRasterZone — the five person raster blocks on the standing brief (BUILD-BRIEF-R7 §6.1).
//
// THE SIBLING OF ProfileZone, NOT A FORK: one `.fgroup` per block with that block's four coverage
// figures beside its head, one row per cell, and a value's evidence opening as a `blockquote.ev`
// with its `.ev-src` exactly as an organisation cell's does (`EvidenceSrc` is ProfileZone's own).
//
// WHAT A PERSON'S RASTER HAS THAT AN ORGANISATION'S DOES NOT: a cell's KIND (§0.2) and six STATES
// the server has already decided (§3.2 — held · pending · open · not applicable · not found ·
// computed). This file DRESSES those states and derives none of them: `state` arrives decided
// from GET /api/persons/:id/profile, so this zone, the People filters and `get_person_raster`
// cannot disagree about the same cell.
//
// THE THREE RULES THIS FILE MUST NOT BREAK (ProfileZone's, unchanged):
//   · A COMPUTED CELL CARRIES NO MARK, and prints its BASIS under the value in the `.fd` voice —
//     the sentence `persons_computed` kept («from title ‘directrice’ at ORG-0012»). ◇ and ▪ are
//     claims; arithmetic makes none.
//   · "not collected" ≠ 0 ≠ "not applicable" ≠ "not found · date". A cell nobody filled renders
//     `fv--unknown` "not collected"; a counted zero renders `fv--nil` as a FIGURE; a cell the rule
//     does not ask of this person reads "not applicable"; a cell a run searched for and did not
//     find reads "not found" with the day it looked. Four states, four dresses, never alike.
//   · AN ESTIMATE SAYS SO: `method === 'estimated'` carries a `.tag--ghost` "estimated" and its
//     confidence in the meta column, never the dress of a value read off a page. Its claim mark
//     is ▪ or ◇ by whether the cell is held — an estimate in Review is ◇, an estimate the operator
//     or the rule held is ▪.
//   · THE MARK IS `cell.held`, NEVER `cell.state === 'held'` (the review of 07.09.2026). The state
//     says a current fact stands on the cell; `held` says `facts.confirmed_by` is not null — the
//     operator's click, or the hold rule's. An import's unconfirmed fact is ◇ on the facts sheet
//     with a Confirm beside it, and drawing it ▪ here made the same fact read two ways on one page.
//
// THE LABELS COME FROM THE MODULE, in the console's language, through `personLabelFor`. Nothing
// here retypes a block, a cell or a token: `src/shared/person-raster.v1.ts` is the one document,
// and a second spelling of `cadre_superieur` in a page would be a second source of truth.
//
// THE VERB. "Fill" starts a `deep_research` run on the person — the SAME function the mast's
// Research verb calls (PersonPage owns it and passes it down), so there is exactly one start path
// and one toast. The zone loads itself and hands the profile back through `onProfile`, so the
// mast can print `buying_influence` beside the tier and the contact card can read its cells
// without a second request; `reloadKey` lets the page ask for a re-read after it wrote something.
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import EmptyState from './EmptyState.js';
import { swissTicks } from './DocumentsZone.js';
import { Att, Ev, Fig, Lnk, Ref, Sep, Sheet, Tag, Zone, ZoneHead } from './PageKit.js';
import { EvidenceSrc } from './ProfileZone.js';
import { useLocale, useT } from '../i18n/index.js';
import { href, ROUTES } from '../routes.js';
import { getPersonProfile } from '../api/persons.js';
import { findPersonBlock, findPersonCell, personLabelFor, type PersonRasterCell } from '../../shared/person-raster.v1.js';
import { QUALIFIER_SIGN, REF_NONE, type NumericQualifier, type RasterLang } from '../../shared/raster.v1.js';
import type { PersonProfileCell, PersonProfileCoverage, PersonProfileResponse } from '../../shared/types.js';
import './PersonRasterZone.de.js';
import './PersonRasterZone.fr.js';

type FmtDate = (iso: string | null | undefined) => string;
type FmtNum = (n: number | string | null | undefined) => string;
type Translate = (key: string, vars?: Record<string, string | number | null | undefined>) => string;

export interface PersonRasterZoneProps {
  personId: number;
  /** Hands the loaded profile back: the mast's tier slot and the contact card read it, no second call. */
  onProfile?: (profile: PersonProfileResponse | null) => void;
  /** THE ONE START VERB — the page's own `research()`; the mast's Research calls the same function. */
  onFill?: () => void;
  filling?: boolean;
  /** Bump it after the page wrote something (the contact card's PATCH) and the raster re-reads. */
  reloadKey?: number;
}

export default function PersonRasterZone({ personId, onProfile, onFill, filling, reloadKey = 0 }: PersonRasterZoneProps) {
  const [profile, setProfile] = useState<PersonProfileResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPersonProfile(personId);
      setProfile(res);
      setError(null);
      onProfile?.(res);
    } catch (err) {
      setProfile(null);
      // The server's own sentence, never a paraphrase.
      setError(err instanceof Error ? err.message : String(err));
      onProfile?.(null);
    } finally {
      setLoading(false);
    }
  }, [personId, onProfile]);

  // `reloadKey` is not read inside `load`; it is in the list so a bump re-runs the effect.
  useEffect(() => { void load(); }, [load, reloadKey]);

  return (
    <PersonRasterZoneView
      personId={personId}
      profile={profile}
      error={error}
      loading={loading}
      onRetry={() => { void load(); }}
      onFill={onFill}
      filling={filling}
    />
  );
}

// ── the view — pure, so a test can hand it a fixture ────────────────────────

export interface PersonRasterZoneViewProps {
  personId: number;
  profile: PersonProfileResponse | null;
  error?: string | null;
  loading?: boolean;
  onRetry?: () => void;
  onFill?: () => void;
  filling?: boolean;
}

export function PersonRasterZoneView({ personId, profile, error = null, loading = false, onRetry, onFill, filling = false }: PersonRasterZoneViewProps) {
  const t = useT();
  const { lang, fmtDate, fmtNum, tp } = useLocale();
  const [openCell, setOpenCell] = useState<string | null>(null);

  const cov = profile?.coverage ?? null;
  const anythingFilled = cov ? cov.held + cov.estimated + cov.computed > 0 : false;

  return (
    <Zone id="raster">
      <ZoneHead
        label={t('Profile')}
        count={cov ? <CoverageFigures coverage={cov} /> : undefined}
        note={profile
          ? (
            <>
              {t('{n} comparable cells · {p} a fill may propose · raster {v}', { n: cov?.total ?? 0, p: profile.proposable, v: profile.raster_version })}
              {' '}<Sep />{' '}
              {profile.computed_at
                ? <>{t('computed')} <Fig>{fmtDate(profile.computed_at)}</Fig></>
                : <span className="fv--unknown" title={t('The night job has not computed this person yet; every computed cell reads "not collected" until it does.')}>{t('not yet computed')}</span>}
            </>
          )
          : t('five blocks of comparable cells — what the record can say about a person, and how it came to say it')}
      >
        {onFill && (
          <button
            type="button"
            className="btn btn-sm"
            disabled={filling}
            title={t('Send an agent to fill this raster: the employer’s own pages, the registers, your own correspondence — never LinkedIn. Every value arrives with its verbatim quote; what the rule can hold is held on arrival, the rest waits in Review.')}
            onClick={onFill}
          >
            {filling ? t('Queuing…') : t('Fill')}
          </button>
        )}
      </ZoneHead>

      {error ? (
        <EmptyState kind="error" title={t('The raster did not answer')} detail={<>GET /api/persons/{personId}/profile — {error}</>} onRetry={onRetry} />
      ) : !profile ? (
        <p className="zone-empty">{loading ? t('Loading…') : t('not collected')}</p>
      ) : (
        <>
          {!anythingFilled && (
            <p className="zone-empty">
              {t('No cell of the raster is filled for this person. "Fill" starts a run; every value it reads comes back with its verbatim quote — a professional cell the rule can verify is held on arrival, everything about the person themselves waits for your click.')}
            </p>
          )}
          <Sheet pad className="facts">
            {profile.blocks.map((block) => {
              const spec = findPersonBlock(block.key);
              return (
                <div className="fgroup" key={block.key}>
                  <div className="fgroup-h u-label">
                    {personLabelFor(lang as RasterLang, block.key)}
                    <span className="zone-note"> · <CoverageFigures coverage={block.coverage} /></span>
                    {spec?.computed && <span className="zone-note"> · {t('computed nightly · no claim mark')}</span>}
                  </div>
                  {block.cells.map((cell) => (
                    <PersonCellRow
                      key={cell.key}
                      cell={cell}
                      open={openCell === cell.key}
                      onToggle={() => setOpenCell((k) => (k === cell.key ? null : cell.key))}
                      lang={lang as RasterLang}
                      fmtDate={fmtDate}
                      fmtNum={fmtNum}
                      tp={tp}
                    />
                  ))}
                </div>
              );
            })}
          </Sheet>
        </>
      )}
    </Zone>
  );
}

// ── the coverage figures — arithmetic, no mark ───────────────────────────────

/** «2 held · 1 estimated · 3 computed · 4 open» — the four figures of §4, per block and whole. */
export function CoverageFigures({ coverage }: { coverage: PersonProfileCoverage }) {
  const t = useT();
  const nil = (n: number): string | undefined => (n === 0 ? 'fv--nil' : undefined);
  return (
    <span title={t('held · estimated · computed · open — counted over the comparable cells asked of this person; a verdict and a cell that does not apply are counted apart')}>
      <Fig className={nil(coverage.held)}>{coverage.held}</Fig> {t('held')}
      {' · '}<Fig className={nil(coverage.estimated)}>{coverage.estimated}</Fig> {t('estimated')}
      {' · '}<Fig className={nil(coverage.computed)}>{coverage.computed}</Fig> {t('computed')}
      {' · '}<Fig className={nil(coverage.open)}>{coverage.open}</Fig> {t('open')}
    </span>
  );
}

// ── one cell ────────────────────────────────────────────────────────────────

export function PersonCellRow({ cell, open, onToggle, lang, fmtDate, fmtNum, tp }: {
  cell: PersonProfileCell;
  open: boolean;
  onToggle: () => void;
  lang: RasterLang;
  fmtDate: FmtDate;
  fmtNum: FmtNum;
  tp: (n: number, one: string, other: string) => string;
}) {
  const t = useT();
  const spec = findPersonCell(cell.key)?.cell ?? null;
  const hasEvidence = !!(cell.evidence && (cell.evidence.quote || cell.evidence.ref));
  const estimated = cell.state === 'held' && cell.method === 'estimated';
  return (
    <div className="fact">
      <span className="fk" title={spec?.sources_hint}>{personLabelFor(lang, cell.block, cell.cell)}</span>
      <span>
        <PersonCellValue cell={cell} lang={lang} fmtNum={fmtNum} tp={tp} />
        {hasEvidence && (
          <>
            {' '}
            <Lnk onClick={onToggle} title={t('Show the verbatim evidence for this cell')}>
              {open ? t('hide evidence') : t('evidence')}
            </Lnk>
          </>
        )}
        {/* THE BASIS — the sentence arithmetic rests on, in the `.fd` voice under the value. */}
        {cell.state === 'computed' && cell.basis && (
          <span className="fd" title={cell.basis}>{cell.basis}</span>
        )}
        {open && hasEvidence && cell.evidence && (
          <Ev src={<EvidenceSrc evidenceKind={cell.evidence.kind} evidenceRef={cell.evidence.ref} fmtDate={fmtDate} asOf={cell.as_of} />}>
            {cell.evidence.quote ?? t('The evidence names a source but keeps no verbatim passage.')}
          </Ev>
        )}
      </span>
      <span className="fmeta">
        {estimated && cell.confidence != null && (
          <>{t('confidence')} <Fig>{cell.confidence.toFixed(2)}</Fig> </>
        )}
        {cell.state === 'held' && cell.method && cell.method !== 'estimated' && <>{t(cell.method)} </>}
        {cell.as_of && cell.state !== 'not_found' ? <Fig>{fmtDate(cell.as_of)}</Fig> : null}
      </span>
    </div>
  );
}

/**
 * The value itself, with its mark. THIS FUNCTION IS THE ONE PLACE the six states are dressed;
 * the contact card renders its cell-backed values through it too, so a cell can never look one
 * way in the raster and another way on the card.
 */
export function PersonCellValue({ cell, lang, fmtNum, tp }: {
  cell: PersonProfileCell;
  lang: RasterLang;
  fmtNum: FmtNum;
  tp: (n: number, one: string, other: string) => string;
}) {
  const t = useT();
  const spec = findPersonCell(cell.key)?.cell ?? null;

  switch (cell.state) {
    case 'not_found':
      return (
        <span className="fv--nil" title={t('A run searched for this cell and found nothing; later runs leave it alone unless asked to refresh.')}>
          {t('not found')}{cell.as_of ? <> · <Fig>{fmtDateShort(cell.as_of)}</Fig></> : null}
        </span>
      );
    case 'not_applicable':
      return (
        <span className="fv--nil" title={t('This cell is not asked of this person: the condition it hangs on — a register row, a rank, a held function — does not hold.')}>
          {t('not applicable')}
        </span>
      );
    case 'pending': {
      const n = cell.pending ?? 1;
      return (
        <>
          <Att kind="proposed" />{' '}
          <Lnk href={href(ROUTES.review({ kind: 'fact', status: 'unheld' }))} title={t('A proposal on this cell waits in Review; nothing is on the record until it is held.')}>
            {tp(n, '{n} proposal in Review', '{n} proposals in Review')}
          </Lnk>
        </>
      );
    }
    default:
      break;
  }

  if (cell.value === null || cell.value.trim() === '') {
    return <span className="fv--unknown">{t('not collected')}</span>;
  }

  const computed = cell.state === 'computed';
  const estimated = cell.method === 'estimated';
  const nil = spec ? isPersonCountedZero(spec, cell.value) : false;
  const rendered = personCellNode(spec, cell.block, cell.cell, cell.type, cell.value, lang, fmtNum, t, cell.qualifier ?? null);

  return (
    <>
      {/* Arithmetic makes no claim: a computed cell carries no ◇ and no ▪. Everything else is ▪
          only once somebody held it — `cell.held` is `facts.confirmed_by`, exactly the test
          ProfileZone's CellValue makes; a current fact an import filed and nobody confirmed is
          the ◇ the facts sheet offers a Confirm on, and it reads ◇ here too. */}
      {!computed && <><Att kind={cell.held ? 'held' : 'proposed'} />{' '}</>}
      <span className={nil ? 'fv--nil' : undefined}>{rendered}</span>
      {estimated && (
        <> <Tag tone="ghost" title={t('The agent inferred this value; it was not read off a page.')}>{t('estimated')}</Tag></>
      )}
    </>
  );
}

/** A `false` boolean and a `0` count are COUNTED, and read as quietly as a zero should. */
export function isPersonCountedZero(cell: Pick<PersonRasterCell, 'type'>, raw: string): boolean {
  if (cell.type === 'bool') return raw === 'false';
  if (cell.type === 'int' || cell.type === 'number') return Number(raw) === 0;
  if (cell.type === 'person_ref' || cell.type === 'organisation_ref') return raw === REF_NONE;
  return false;
}

/** 2026-09-05 → 05.09.2026, for the "not found · date" verdict. */
const fmtDateShort = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
};

const isUrl = (v: string): boolean => /^https?:\/\//i.test(v);
const isEmail = (v: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v);
const isPhone = (v: string): boolean => /^\+?[\d\s().-]{7,}$/.test(v);

/**
 * A stored cell value as a reader sees it. Enum, band and multi values come back through
 * `personLabelFor` in the console's language; a quantity keeps its unit and its qualifier sign;
 * a year stays a year. Never a raw token on the page.
 */
export function personCellText(
  cell: Pick<PersonRasterCell, 'type' | 'unit'> | null,
  block: string,
  cellKey: string,
  type: PersonProfileCell['type'],
  raw: string,
  lang: RasterLang,
  fmtNum: FmtNum,
  t: Translate,
  qualifier: NumericQualifier | null = null,
): string {
  switch (type) {
    case 'enum':
    case 'band':
      return personLabelFor(lang, block, cellKey, raw);
    case 'enum_multi':
      return raw.split(',').map((v) => personLabelFor(lang, block, cellKey, v.trim())).join(' · ');
    case 'bool':
      return raw === 'true' ? t('yes') : raw === 'false' ? t('no') : raw;
    case 'int':
    case 'number': {
      const n = swissTicks(fmtNum(raw));
      const sign = qualifier ? `${QUALIFIER_SIGN[qualifier]} ` : '';
      return cell?.unit ? `${sign}${n} ${cell.unit}` : `${sign}${n}`;
    }
    case 'person_ref':
    case 'organisation_ref':
      return raw === REF_NONE ? t('none') : raw;
    default:
      return raw;
  }
}

/**
 * THE VOICES OF AN IDENTIFIER (the mockup's own): an e-mail and a LinkedIn URL are identities and
 * read mono (`Ref`), the URL as a link; a phone number is a figure (`Fig`) and dials; anything
 * else is the text as the source spelt it. Exported for the contact card, which prints the same
 * values from the person's columns.
 */
export function IdentifierValue({ value }: { value: string }) {
  if (isUrl(value)) {
    return <a className="lnk" href={value} target="_blank" rel="noreferrer noopener"><Ref>{value.replace(/^https?:\/\/(www\.)?/, '')}</Ref></a>;
  }
  if (isEmail(value)) return <a href={`mailto:${value}`}><Ref>{value}</Ref></a>;
  if (isPhone(value)) return <a href={`tel:${value.replace(/[\s().-]/g, '')}`}><Fig>{value}</Fig></a>;
  return <>{value}</>;
}

function personCellNode(
  spec: PersonRasterCell | null,
  block: string,
  cellKey: string,
  type: PersonProfileCell['type'],
  raw: string,
  lang: RasterLang,
  fmtNum: FmtNum,
  t: Translate,
  qualifier: NumericQualifier | null,
): ReactNode {
  if (type === 'text') return <IdentifierValue value={raw} />;
  return personCellText(spec, block, cellKey, type, raw, lang, fmtNum, t, qualifier);
}

/** `cellOf(profile, 'job', 'buying_influence')` — the one lookup the page and the card share. */
export function cellOf(profile: PersonProfileResponse | null | undefined, block: string, cell: string): PersonProfileCell | null {
  if (!profile) return null;
  const b = profile.blocks.find((x) => x.key === block);
  return b?.cells.find((c) => c.cell === cell) ?? null;
}
